"use client";
import { cn } from "@/lib/utils";
import Link, { LinkProps } from "next/link";
import React, { useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IconX } from "@tabler/icons-react";
import { AlertCircle, ArrowLeft, ArrowRight, BadgeDollarSign, Bell, Home, HomeIcon, List, LogOut, MessageSquare, Package, Settings, User, Wallet } from "lucide-react";
import { usePathname, useRouter } from 'next/navigation';
import { signOut, useSession } from "next-auth/react";
import Icons from "./icons";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Badge } from "./badge";
import moment from "moment-jalaali";

interface Links {
  label: string;
  href: string;
  subLinks: any;
  icon: React.JSX.Element | React.ReactNode;
}

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const useSidebar = () => {



  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate: animate }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};


export const SidebarBody = ({
  className,
  children,
  notifs,
  ...props
}: React.ComponentProps<typeof motion.div> & { notifs?: any; children: React.ReactNode }) => {
  return (
    <>
      <DesktopSidebar className={className} notifs={notifs} {...props}>
        {children}
      </DesktopSidebar>
      <MobileSidebar className={className} notifs={notifs} {...(props as React.ComponentProps<"div">)}>
        {children}
      </MobileSidebar>
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  notifs,
  ...props
}: React.ComponentProps<typeof motion.div> & { notifs?: any; children: React.ReactNode }) => {
  const { open, setOpen, animate } = useSidebar();

  const SideBarOpenClose = () => {
    if (open === true) {
      setOpen(false)
    } else {
      setOpen(true)
    }

  }
  return (
    <>
      <motion.div
        // sidebar
        className={cn(
          "h-full py-5 hidden  md:flex md:flex-col bg-[#191620] w-[220px] rounded-xl flex-shrink-0 relative",
          className
        )}
        animate={{
          width: animate ? (open ? "220px" : "70px") : "220px",
        }}
        {...props}
      >
        <>


          <div className="transform  -translate-x-[43px] absolute z-30 top-[46%] left-0 cursor-pointer group " onClick={SideBarOpenClose}>
            <div className="relative w-full h-full " >
              <div className={cn("absolute z-40 pr-3 left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-transform duration-300", open ? "group-hover:-translate-x-[11px] " : "group-hover:-translate-x-[21px] ")}>
                {open ? <ArrowRight size={19} /> : <ArrowLeft size={19} />}
              </div>

              {open ? (
                <svg className="transform rotate-90" width={100} height={62} xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 144 62">
                  <g>
                    <g id="Layer_1">
                      <path
                        fill="#0A0512"
                        d="M111.3,31.9c-10.6-7.8-18.5-17.8-38.7-17.8h-1.2c-20.2,0-28.1,10-38.7,17.8C24.9,39.1,13.2,40.7,0,40.9v10.4h144v-10.4c-13.2-.3-24.9-1.8-32.7-9Z"
                      />
                    </g>
                  </g>
                </svg>
              ) :
                (
                  <svg className="transform rotate-[-90deg] -translate-x-[13px]" width={100} height={62} xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 144 62">
                    <g>
                      <g id="Layer_1">
                        <path
                          fill="#191620"
                          d="M111.3,31.9c-10.6-7.8-18.5-17.8-38.7-17.8h-1.2c-20.2,0-28.1,10-38.7,17.8C24.9,39.1,13.2,40.7,0,40.9v10.4h144v-10.4c-13.2-.3-24.9-1.8-32.7-9Z"
                        />
                      </g>
                    </g>
                  </svg>
                )
              }
            </div>
          </div>



          <div className="absolute top-0 right-0 w-[110px] h-[110px] bg-[#6f48a1] z-40 blur-[80px] transform  -translate-y-8 translate-x-2"></div>


          {children}
        </>
      </motion.div>
    </>
  );
};

export const MobileSidebar = ({
  className,
  children,
  notifs,
  ...props
}: React.ComponentProps<"div"> & { notifs?: any; children: React.ReactNode }) => {
  const { data: session } = useSession()
  const { open, setOpen } = useSidebar();
  const router = useRouter()
  return (
    <>
      <div
        className={cn(
          "h-10 px-4 py-8 flex flex-row md:hidden fixed top-0 z-50  items-center justify-between w-full ", !open && "bg-[#19162000] backdrop-blur-lg"
        )}
        {...props}
      >
        <div className="flex justify-between z-20 w-full ">

          <div className="flex items-center gap-2">
            <div onClick={() => setOpen(!open)} className="cursor-pointer backdrop-blur-lg bg-gradient-to-tr from-transparent via-[rgba(98,89,117,0.43)] to-transparent rounded-md p-2 shadow transition-[background-position] duration-1000 ease-in-out  hover:bg-[length:200%_200%] hover:bg-[position:100%_0]">
              <Icons.burder />
            </div>

            <Link href="/">
              <div  className="cursor-pointer backdrop-blur-lg bg-gradient-to-tr from-transparent via-[rgba(98,89,117,0.43)] to-transparent rounded-md p-2 shadow transition-[background-position] duration-1000 ease-in-out  hover:bg-[length:200%_200%] hover:bg-[position:100%_0]">
                <HomeIcon className="text-gray-500" />
              </div>
            </Link>

          </div>

          <div className="flex items-center gap-2">

            {/* notification */}
            <Popover>
              <PopoverTrigger asChild>
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.1 }}
                  className="relative cursor-pointer p-2 rounded-xl bg-white/10 backdrop-blur-lg border border-white/10"
                >
                  <Bell className="text-gray-300 w-5 h-5" />
                  {notifs?.filter((n: any) => !n.isRead).length > 0 && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>
                  )}
                </motion.div>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="center"
                sideOffset={10}
                className="w-80 bg-[#1A1525]/60 backdrop-blur-xl border border-white/10 text-white shadow-xl shadow-purple-500/10 rounded-xl p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold flex items-center gap-2"><Bell className="w-5 h-5" /> اعلان‌ها</h3>
                  <button
                    onClick={() => router.push('/dashboard/notifications')}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    مشاهده همه
                  </button>
                </div>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {notifs?.length > 0 ? (
                    notifs.map((notification: any) => (
                      <div
                        key={notification._id}
                        className={`p-3 rounded-lg transition-all duration-300 ${notification.isRead
                          ? "bg-white/5"
                          : "bg-white/10 border border-white/20"
                          }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-gray-300">
                            {notification.type === "system" && <AlertCircle className="w-5 h-5" />}
                            {notification.type === "order" && <Package className="w-5 h-5" />}
                            {notification.type === "wallet" && <Wallet className="w-5 h-5" />}
                            {notification.type === "support" && <MessageSquare className="w-5 h-5" />}
                          </span>
                          <div className="flex-1 space-y-1">
                            <p className="text-sm">{notification.message}</p>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={`${notification.priority === "high"
                                  ? "bg-red-500/10 text-red-500 border-red-500/20"
                                  : notification.priority === "medium"
                                    ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/20"
                                    : "bg-green-500/10 text-green-500 border-green-500/20"
                                  }`}
                              >
                                {notification.priority === "high" && "بالا"}
                                {notification.priority === "medium" && "متوسط"}
                                {notification.priority === "low" && "پایین"}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {moment(notification.createdAt).fromNow()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-400 text-sm">
                      هیچ اعلانی وجود ندارد
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* wallet */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-lg border border-white/10"
                >
                  <p className="text-sm">0 تومان</p>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={10}
                className="w-48 bg-[#1A1525]/30 backdrop-blur-xl border border-white/10 text-white shadow-xl shadow-purple-500/10 rounded-xl"
              >
                <DropdownMenuItem className="cursor-pointer focus:bg-white/10 px-3 py-3 rounded-lg">
                  <BadgeDollarSign className="w-7 h-7  text-gray-300" />
                  شارژ حساب
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer px-3 py-3 focus:bg-white/10 rounded-lg">
                  <List className="w-7 h-7 text-gray-300" />
                  لیست تراکنش
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>


            {/* profile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="cursor-pointer flex items-center gap-2 p-2 rounded-lg bg-white/10 backdrop-blur-lg border border-white/10"
                >
                  <User className="text-gray-300 w-5 h-5" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={10}
                className="w-48 bg-[#1A1525]/30 backdrop-blur-xl border border-white/10 text-white shadow-xl shadow-purple-500/10 rounded-xl"
              >

                <DropdownMenuItem className="focus:bg-white/10 bg-zinc-900/40 px-3 py-3 rounded-lg flex items-center justify-center">
                  {session?.user ? (session?.user?.phone ? session?.user?.phone : session?.user?.email) : "در حال بروزرسانی"}
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer focus:bg-white/10 px-3 py-3 rounded-lg" asChild>
                  <Link href="/dashboard" className="flex items-center">
                    <Settings className="w-4 h-4 ml-2" />
                    تنظیمات
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer focus:bg-white/10 px-3 py-3 rounded-lg" asChild>
                  <Link href="/" className="flex items-center">
                    <Home className="w-4 h-4 ml-2" />
                    صفحه اصلی
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer px-3 py-3 focus:bg-white/10 rounded-lg text-red-400">
                  <LogOut className="w-4 h-4 ml-2" />
                  خروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>


          </div>

        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
              className={cn(
                "fixed h-full w-full inset-0 p-10 z-[100] flex flex-col justify-between bg-[#191620b4] backdrop-blur-3xl",
                className
              )}
            >
              <div
                className="absolute left-10 top-10 z-50 text-neutral-200"
                onClick={() => setOpen(!open)}
              >
                <IconX />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export const SidebarLink = ({
  link,
  className,
  ...props
}: {
  link: Links;
  className?: string;
  props?: LinkProps;
}) => {
  const { open, setOpen, animate } = useSidebar();
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const pathname = usePathname();

  const isActive = pathname === link.href || (link.subLinks?.some((sub: { href: string; }) => pathname === sub.href));
  const isInSubLinks = link.subLinks?.some((sub: { href: string; }) => pathname === sub.href);

  React.useEffect(() => {
    if (!isInSubLinks) {
      setOpenSubMenu(false);
    }
  }, [pathname]);

  const baseClasses = cn(
    "flex items-center justify-between gap-3 group/sidebar py-2 px-4 relative",
    isActive &&
    "backdrop-blur-lg bg-gradient-to-tr from-transparent via-[rgba(98,89,117,0.43)] to-transparent rounded-sm p-2 shadow mx-2",
    openSubMenu &&
    "backdrop-blur-lg bg-gradient-to-tr from-transparent via-[rgba(98,89,117,0.43)] to-transparent rounded-sm p-2 shadow mx-2",
    className
  );

  const handleClick = () => {
    if (link.subLinks) {
      setOpenSubMenu((prev) => !prev);
    } else if (window.innerWidth < 768) {
      setOpen(false);
    }
  };

  return (
    <div className="relative w-full">
      {/* main */}
      <Link
        href={link.href || "#"}
        className={baseClasses}
        {...props}
        onClick={handleClick}
      >
        <div
          className={cn(
            "absolute rounded-sm",
            isActive &&
            (open
              ? "w-[3px] h-[23px] bg-[#412961] ml-1 right-0"
              : "w-[23px] h-[2px] bg-[#412961] mr-[6px] bottom-0")
          )}
        ></div>

        <div className="flex items-center justify-center rounded-sm ml-1 gap-3">
          <div
            className={cn(
              link.label !== 'خروج' && "opacity-60",
              !open && "border border-input bg-accent/20 w-9 h-9 flex items-center justify-center rounded-sm ml-1",
              isActive && (!open ? "text-secondary-foreground border-none opacity-100 font-normal" : "opacity-100")
            )}
          >
            {link.icon}
          </div>

          <motion.span
            animate={{
              display: animate ? (open ? "inline-block" : "none") : "inline-block",
              opacity: animate ? (open ? 1 : 0) : 1,
            }}
            className="text-[14px] group-hover/sidebar:translate-x-1 transition duration-150 whitespace-pre inline-block !p-0 !m-0"
          >
            <div className={cn("text-[#F9F9FA] opacity-60", isActive && "font-bold opacity-100")}>
              {link.label}
            </div>
          </motion.span>

        </div>
        <div>
          {link.subLinks?.length > 0 && open && (
            <>
              {openSubMenu ? <Icons.arrowdown /> : <Icons.arrowleft />}
            </>
          )}
        </div>
      </Link>

      {/* drop menu */}
      {link.subLinks && open && (
        <div className="mr-6">
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: openSubMenu ? "auto" : 0, opacity: openSubMenu ? 1 : 0 }}
            className="overflow-hidden"
          >
            {link.subLinks.map((subLink: any, index: number) => {
              const isSubLinkActive = pathname === subLink.href;
              return (
                <Link
                  key={index}
                  href={subLink.href}
                  className="relative flex items-end pt-4 px-4"
                  onClick={() => window.innerWidth < 768 && setOpen(false)} // اضافه کردن این خط
                >
                  <div className="absolute right-0 inset-x-0 translate-x-6 -translate-y-1 z-0">
                    <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                      <path d="M25 0 V35 Q25 45 15 45" fill="transparent" stroke="#363043" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span
                    className={cn(
                      "mr-2 text-xs z-10 w-full transition-colors duration-500",
                      isSubLinkActive ? "text-white font-bold" : "text-gray-400 hover:text-white"
                    )}
                  >
                    {subLink.label}
                  </span>
                </Link>
              );
            })}
          </motion.div>
        </div>
      )}

      {!open && link.subLinks && openSubMenu && (
        <motion.div
          initial={{ x: 10, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 20, opacity: 0 }}
          className="absolute top-0 left-10 inset-x-0 mr-[77px] bg-[#1a1525]/60 transition-all duration-300 text-sm text-white rounded-lg shadow-xl border border-[#35214e] backdrop-blur-lg w-48 p-3 z-[200]"
        >
          {link.subLinks.map((subLink: any, index: number) => {
            const isSubLinkActive = pathname === subLink.href;
            return (
              <Link
                key={index}
                href={subLink.href}
                className={cn(
                  "block px-4 py-2 my-1 hover:bg-[#312347] text-gray-400 hover:text-white rounded-md transition-colors duration-300",
                  isSubLinkActive && "text-white font-bold bg-gradient-to-tr from-transparent via-[rgba(98,89,117,0.43)] to-transparent"
                )}
              >
                {subLink.label}
              </Link>
            );
          })}
        </motion.div>
      )}
    </div>
  );
};